// ═══════════════════════════════════════
// Campana de notificaciones — header global
// ═══════════════════════════════════════
// Muestra un ícono de campana con badge de no-leídas. Al clickear abre un panel
// con las últimas 30 notificaciones. Suscripción en tiempo real vía onSnapshot.
//
// Depende de lib/notificaciones.js (estructura de doc) pero NO de los helpers
// async: usamos onSnapshot directo para que el badge sea live.
// ═══════════════════════════════════════

import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore'
import { db } from '@core/lib/firebase'
import { docConId } from '@core/lib/docConId'
import { COLLECTIONS } from '@core/lib/constants'
import { useAuth } from '@core/contexts/AuthContext'
import {
  marcarLeida, marcarTodasLeidas, destinoDeNotificacion,
  NOTIFICACION_PRIORIDADES, NOTIFICACION_TIPOS,
} from '@core/lib/notificaciones'

const IconBell = ({ filled = false }) => (
  <svg viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
)

// Map de prioridad → clase de borde izquierdo del item
const PRIORIDAD_BORDER = {
  [NOTIFICACION_PRIORIDADES.CRITICA]: 'border-l-red-500',
  [NOTIFICACION_PRIORIDADES.ALTA]:    'border-l-amber-400',
  [NOTIFICACION_PRIORIDADES.NORMAL]:  'border-l-sky-400',
  [NOTIFICACION_PRIORIDADES.BAJA]:    'border-l-zinc-500',
}

// Icono chico por tipo (más ameno que texto)
function iconoPorTipo(tipo) {
  switch (tipo) {
    case NOTIFICACION_TIPOS.SLA_VENCIDO:
    case NOTIFICACION_TIPOS.SLA_POR_VENCER:
      return '⏱️'
    case NOTIFICACION_TIPOS.TAREA_ASIGNADA:
    case NOTIFICACION_TIPOS.TAREA_ACEPTADA:
    case NOTIFICACION_TIPOS.TAREA_RECHAZADA:
      return '✅'
    case NOTIFICACION_TIPOS.MENSAJE_MENCION:
      return '📣'
    case NOTIFICACION_TIPOS.MENSAJE:
      return '💬'
    case NOTIFICACION_TIPOS.EMPRESA_ESTADO:
      return '🏢'
    default:
      return '•'
  }
}

function formatoRelativo(ts) {
  if (!ts) return ''
  const d = ts.toDate ? ts.toDate() : new Date(ts)
  const diff = Date.now() - d.getTime()
  if (diff < 60_000) return 'hace segundos'
  if (diff < 3_600_000) return `hace ${Math.floor(diff / 60_000)}m`
  if (diff < 86_400_000) return `hace ${Math.floor(diff / 3_600_000)}h`
  if (diff < 604_800_000) return `hace ${Math.floor(diff / 86_400_000)}d`
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })
}

export default function NotificacionesBell() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const panelRef = useRef(null)
  const btnRef = useRef(null)

  // Subscripción en tiempo real (últimas 30 notif, leídas o no).
  useEffect(() => {
    if (!user?.uid) { setItems([]); setLoading(false); return }
    const q = query(
      collection(db, COLLECTIONS.NOTIFICACIONES),
      where('userId', '==', user.uid),
      orderBy('creadoEn', 'desc'),
      limit(30),
    )
    const unsub = onSnapshot(q, snap => {
      setItems(snap.docs.map(docConId))
      setLoading(false)
    }, err => {
      console.error('NotificacionesBell onSnapshot:', err)
      setLoading(false)
    })
    return unsub
  }, [user?.uid])

  // Click-outside para cerrar panel
  useEffect(() => {
    if (!open) return
    function onClick(e) {
      if (!panelRef.current || !btnRef.current) return
      if (panelRef.current.contains(e.target) || btnRef.current.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const noLeidas = items.filter(i => !i.leida)
  const countBadge = noLeidas.length

  async function handleClickItem(n) {
    if (!n.leida) {
      try { await marcarLeida(n.id) } catch (e) { console.warn(e) }
    }
    setOpen(false)
    // El link es un string PERSISTIDO: los guardados antes del fix del report
    // c3mwYtc8Q8vds7ZkyWru no llevan `?empresa=` y son un callejón sin salida
    // para su destinatario. `destinoDeNotificacion` lo repara con el
    // `empresaId` que el mismo doc ya trae (ver notificaciones.js).
    const destino = destinoDeNotificacion(n)
    if (destino) navigate(destino)
  }

  async function handleMarcarTodas() {
    if (!user?.uid) return
    try { await marcarTodasLeidas(user.uid) } catch (e) { console.warn(e) }
  }

  if (!user?.uid) return null

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        title={countBadge > 0 ? `${countBadge} notificaciones sin leer` : 'Notificaciones'}
        aria-label="Notificaciones"
        className={`relative inline-flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-200 ${
          countBadge > 0
            ? 'text-brand-accent bg-brand-accent/10 border border-brand-accent/30 hover:bg-brand-accent/15'
            : 'text-zinc-400 bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.06] hover:text-zinc-200'
        }`}
      >
        <IconBell filled={countBadge > 0} />
        {countBadge > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-brand-bg">
            {countBadge > 99 ? '99+' : countBadge}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 top-full mt-2 w-[380px] max-w-[92vw] bg-zinc-900 !p-0 rounded-2xl z-50 shadow-2xl border border-white/[0.08] overflow-hidden"
          role="dialog"
        >
          {/* Header del panel */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
            <div className="text-[13px] font-semibold text-zinc-200">
              Notificaciones
              {countBadge > 0 && (
                <span className="ml-2 text-[11px] text-brand-accent font-normal">
                  {countBadge} sin leer
                </span>
              )}
            </div>
            {countBadge > 0 && (
              <button
                onClick={handleMarcarTodas}
                className="text-[11px] text-zinc-400 hover:text-white transition"
              >
                Marcar todas leídas
              </button>
            )}
          </div>

          {/* Lista */}
          <div className="max-h-[480px] overflow-y-auto">
            {loading ? (
              <div className="py-10 text-center text-zinc-500 text-[12px]">Cargando...</div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-zinc-500 text-[12px]">
                Sin notificaciones
              </div>
            ) : (
              items.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleClickItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-white/[0.04] last:border-b-0 border-l-4 transition ${
                    PRIORIDAD_BORDER[n.prioridad] || 'border-l-zinc-700'
                  } ${n.leida ? 'bg-transparent hover:bg-white/[0.02]' : 'bg-brand-accent/[0.04] hover:bg-brand-accent/[0.08]'}`}
                >
                  <div className="flex items-start gap-2.5">
                    <span className="text-[16px] leading-none mt-0.5 shrink-0">{iconoPorTipo(n.tipo)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <div className={`text-[13px] ${n.leida ? 'text-zinc-300' : 'text-white font-semibold'} truncate flex-1`}>
                          {n.titulo}
                        </div>
                        {!n.leida && (
                          <span className="w-1.5 h-1.5 rounded-full bg-brand-accent mt-1.5 shrink-0" />
                        )}
                      </div>
                      {n.cuerpo && (
                        <div className="text-[12px] text-zinc-500 mt-0.5 line-clamp-2">
                          {n.cuerpo}
                        </div>
                      )}
                      <div className="text-[10px] text-zinc-600 mt-1">
                        {formatoRelativo(n.creadoEn)}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
